import { z } from 'zod';

/** RFC 9562 UUID (the API issues UUIDv7 identifiers). */
export const uuidSchema = z.uuid('Must be a valid UUID');

/** Trimmed, lower-cased e-mail address. */
export const emailSchema = z
  .string('Email is required')
  .trim()
  .toLowerCase()
  .min(3, 'Email is required')
  .max(254, 'Email is too long')
  .pipe(z.email('Must be a valid email address'));

export const nonEmptyTrimmed = (max: number, label = 'Value') =>
  z
    .string(`${label} is required`)
    .trim()
    .min(1, `${label} is required`)
    .max(max, `${label} must be at most ${max} characters`);

/** Optional free text: empty strings are normalized to null so they can clear a column. */
export const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Must be at most ${max} characters`)
    .transform((v) => (v.length === 0 ? null : v))
    .nullable()
    .optional();

/**
 * Master-data codes: trimmed, upper-cased; letters, digits, space, dot, dash,
 * underscore and slash (course codes such as "NCM 101" are common).
 */
export const codeSchema = (max = 40) =>
  z
    .string('Code is required')
    .trim()
    .toUpperCase()
    .min(1, 'Code is required')
    .max(max, `Code must be at most ${max} characters`)
    .regex(/^[A-Z0-9][A-Z0-9 ._\-/]*$/, 'Code may contain letters, digits, space, . _ - /');

/** Calendar date in ISO format (YYYY-MM-DD). */
export const isoDateSchema = z.iso.date('Must be a date in YYYY-MM-DD format');

/** Wall-clock time "HH:mm" (24-hour). */
export const timeOfDaySchema = z
  .string('Time is required')
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Must be a time in HH:mm (24-hour) format');

export const latitudeSchema = z.number().min(-90, 'Latitude must be ≥ -90').max(90, 'Latitude must be ≤ 90');
export const longitudeSchema = z
  .number()
  .min(-180, 'Longitude must be ≥ -180')
  .max(180, 'Longitude must be ≤ 180');

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const timeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine(isValidTimeZone, 'Must be a valid IANA time zone, e.g. Asia/Manila');

/** Converts "HH:mm" to minutes after midnight. */
export function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/** Shared refinement: effectiveTo must not precede effectiveFrom. */
export function refineEffectiveRange(
  value: { effectiveFrom?: string | null; effectiveTo?: string | null },
  ctx: z.RefinementCtx,
): void {
  if (value.effectiveFrom && value.effectiveTo && value.effectiveTo < value.effectiveFrom) {
    ctx.addIssue({
      code: 'custom',
      path: ['effectiveTo'],
      message: 'Effective end date must be on or after the effective start date',
    });
  }
}

export const effectiveDatesShape = {
  effectiveFrom: isoDateSchema.nullable().optional(),
  effectiveTo: isoDateSchema.nullable().optional(),
};

// ── Query-string helpers (§56: search, sort, filter, pagination) ──────────────
export const MAX_PAGE_SIZE = 100;

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(20),
  q: z.string().trim().max(100).optional(),
  /** `field:asc` or `field:desc`; the API validates field names per resource. */
  sort: z
    .string()
    .trim()
    .regex(/^[a-zA-Z][a-zA-Z0-9_.]*:(asc|desc)$/, 'Sort must look like field:asc or field:desc')
    .optional(),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/** Boolean query parameter accepting true/false/1/0/yes/no. */
export const booleanQuerySchema = z.stringbool();

export const idParamSchema = z.object({ id: uuidSchema });
